import ExcelJS from 'exceljs';
import type { Container } from '@/types/container';

interface ExportColumn {
    id: string;
    label: string;
}

const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getCellValue = (container: Container, colId: string): string | number => {
    switch (colId) {
        case 'containerNumber': return container.containerNumber || '';
        case 'route': return `${container.route?.from || ''} → ${container.route?.to || ''}`;
        case 'shippingCompany': return container.shippingCompany || '';
        case 'progress': return container.progress ?? '';
        case 'etd': return formatDate(container.estimatedDepartureDate);
        case 'eta': return formatDate(container.eta);
        case 'physicalStatus': return container.physicalStatus || '';
        case 'riskLevel': return container.riskLevel || '';
        case 'documentCount': return container.documentCount ?? '';
        case 'documentStatusOverall': return container.docStatusMessage || container.documentStatusOverall || '';
        case 'exogenStatusOverall': return container.exogenStatusOverall || '';
        case 'lastUpdate': return formatDate(container.lastUpdateAt);
        case 'bookingNumber': return container.bookingNumber || '';
        case 'blNumber': return container.blNumber || '';
        case 'value': return container.totalValueUsd ?? container.value?.amount ?? '';
        case 'product': return container.commodity || '';
        case 'quantity': return container.totalWeightKg ? `${container.totalWeightKg} kg` : '';
        case 'temperature': return container.temperatureSetpointCelsius ?? '';
        case 'actualDepartureDate': return formatDate(container.actualDepartureDate);
        case 'delayDays': {
            if (!container.estimatedDepartureDate || !container.actualDepartureDate) return '';
            const etd = new Date(container.estimatedDepartureDate);
            const actual = new Date(container.actualDepartureDate);
            return Math.ceil((actual.getTime() - etd.getTime()) / (1000 * 60 * 60 * 24));
        }
        case 'originPort': return container.route?.from || '';
        case 'destinationPort': return container.route?.to || '';
        default: return '';
    }
};

function buildRows(containers: Container[], columns: ExportColumn[]) {
    return containers.map(c =>
        Object.fromEntries(columns.map(col => [col.label, getCellValue(c, col.id)]))
    );
}

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function escapeCsvCell(value: string | number): string {
    const s = String(value ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

export async function exportContainers(
    containers: Container[],
    columns: ExportColumn[],
    format: 'csv' | 'xlsx'
) {
    const rows = buildRows(containers, columns);
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `containers_${timestamp}.${format}`;

    if (format === 'csv') {
        const headers = columns.map(col => col.label);
        const lines = [headers.map(escapeCsvCell).join(',')];
        for (const row of rows) {
            lines.push(headers.map(h => escapeCsvCell(row[h] ?? '')).join(','));
        }
        const csv = lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        downloadBlob(blob, filename);
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Containers', {});

    sheet.columns = columns.map(col => {
        const maxLen = Math.min(
            40,
            Math.max(
                col.label.length,
                ...containers.map(c => String(getCellValue(c, col.id)).length)
            ) + 2
        );
        return { header: col.label, key: col.label, width: maxLen };
    });

    sheet.addRows(rows);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, filename);
}
